"use client";

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppStore } from "@/stores";

/** Returns true when a text-editing element has focus. */
function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if ((el as HTMLElement).isContentEditable) return true;
  if (el.closest(".ProseMirror")) return true;
  return false;
}

interface UseQuickActionShortcutsOptions {
  /** Called when the user triggers the Schedule Meeting shortcut (⌘⇧M / Ctrl+Shift+M). */
  onScheduleMeeting: () => void;
}

/**
 * Registers global modifier-key shortcuts for quick actions.
 *
 * Scheme: ⌘⇧ (Mac) / Ctrl+Shift (Windows/Linux) + letter.
 * | Shortcut       | Action                    |
 * | ⌘⇧U / Ctrl⇧U  | Navigate /upload          |
 * | ⌘⇧E / Ctrl⇧E  | Navigate /upload/record   |
 * | ⌘⇧S / Ctrl⇧S  | Navigate /recorder/create |
 * | ⌘⇧M / Ctrl⇧M  | Open Schedule Meeting     |
 *
 * Note: Record uses E, not R. ⌘⇧R / Ctrl+Shift+R is the browser's
 * hard-reload shortcut and must not be overridden.
 *
 * Detection uses `e.code` (e.g. "KeyU") rather than `e.key` (e.g. "U" or "u")
 * because `e.code` is layout-independent: it reflects the physical key position
 * regardless of OS, locale, or Shift state, so it works correctly on AZERTY,
 * Dvorak, and other non-QWERTY layouts without case-normalisation.
 */
export function useQuickActionShortcuts({ onScheduleMeeting }: UseQuickActionShortcutsOptions) {
  const navigate = useNavigate();
  const openCaptureDialog = useAppStore((s) => s.openCaptureDialog);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return;
      if (isInputFocused()) return;

      const folderId = window.location.pathname.match(/^\/folder\/([^/?]+)/)?.[1];

      switch (e.code) {
        case "KeyU":
          e.preventDefault();
          openCaptureDialog({ mode: "upload", folderId });
          break;
        case "KeyL":
          e.preventDefault();
          openCaptureDialog({ mode: "translate", folderId });
          break;
        case "KeyE":
          e.preventDefault();
          navigate("/upload/record");
          break;
        case "KeyS":
          e.preventDefault();
          navigate("/recorder/create");
          break;
        case "KeyM":
          e.preventDefault();
          onScheduleMeeting();
          break;
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigate, onScheduleMeeting, openCaptureDialog]);
}
